import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Globe, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import EuropeMap from '@/components/EuropeMap';
import CzechMap from '@/components/CzechMap';

type MapView = 'europe' | 'cz';

const FloatingParticle = ({ delay, x, y }: { delay: number; x: string; y: string }) => (
  <motion.div
    className="absolute w-1 h-1 rounded-full bg-primary/20"
    style={{ left: x, top: y }}
    animate={{
      y: [0, -30, 0],
      x: [0, 15, 0],
      opacity: [0.2, 0.5, 0.2],
    }}
    transition={{
      duration: 6 + delay,
      repeat: Infinity,
      ease: 'easeInOut',
      delay,
    }}
  />
);

const WorldMapSection = () => {
  const { lang, t } = useLanguage();
  const [activeCountries, setActiveCountries] = useState<string[]>(['CZ']);
  const [view, setView] = useState<MapView>('europe');
  const mapTexts = t.map;

  useEffect(() => {
    const fetchCountries = async () => {
      const { data } = await supabase
        .from('delivery_countries')
        .select('country_code');
      if (data) {
        const codes = data.map((d: { country_code: string }) => d.country_code.toLowerCase());
        if (!codes.includes('cz')) codes.push('cz');
        setActiveCountries(codes);
      }
    };
    fetchCountries();
  }, []);

  const particles = [
    { delay: 0, x: '15%', y: '20%' },
    { delay: 1.2, x: '80%', y: '30%' },
    { delay: 2.5, x: '25%', y: '70%' },
    { delay: 0.8, x: '70%', y: '75%' },
    { delay: 3.1, x: '50%', y: '15%' },
    { delay: 1.8, x: '90%', y: '55%' },
    { delay: 0.4, x: '10%', y: '50%' },
    { delay: 2.2, x: '60%', y: '85%' },
  ];

  return (
    <section className="py-20 md:py-32 relative overflow-hidden">
      {/* Grid pattern background */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="map-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="hsl(var(--primary))" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#map-grid)" />
        </svg>
      </div>

      {/* Radial gradient overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-primary/[0.04] rounded-full blur-[120px]" />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-primary/[0.03] rounded-full blur-[100px]" />
      </div>

      {/* Animated light streak */}
      <motion.div
        className="absolute top-0 left-0 w-[2px] h-full bg-gradient-to-b from-transparent via-primary/10 to-transparent pointer-events-none"
        animate={{ x: ['-10%', '110%'] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear', repeatDelay: 4 }}
      />

      {/* Floating particles */}
      {particles.map((p, i) => (
        <FloatingParticle key={i} {...p} />
      ))}

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2
            className="text-4xl md:text-5xl lg:text-6xl tracking-wider text-foreground mb-4"
            style={{ fontFamily: 'Bebas Neue' }}
          >
            {mapTexts.title}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {mapTexts.subtitle}
          </p>
        </motion.div>

        {/* Toggle buttons */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Button
            variant={view === 'europe' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('europe')}
            className="gap-2"
          >
            <Globe className="w-4 h-4" />
            {mapTexts.world}
          </Button>
          <Button
            variant={view === 'cz' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('cz')}
            className="gap-2"
          >
            <MapPin className="w-4 h-4" />
            {mapTexts.czechRepublic}
          </Button>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="relative max-w-5xl mx-auto"
        >
          <AnimatePresence mode="wait">
            {view === 'europe' ? (
              <motion.div
                key="europe"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl p-4 backdrop-blur-sm bg-card/10 border border-primary/[0.08] shadow-[0_0_40px_-10px_hsl(var(--primary)/0.1)]"
              >
                <EuropeMap activeCountries={activeCountries} />
              </motion.div>
            ) : (
              <motion.div
                key="cz"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl p-4 md:p-8 backdrop-blur-sm bg-card/10 border border-primary/[0.08] shadow-[0_0_40px_-10px_hsl(var(--primary)/0.1)]"
              >
                <CzechMap />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mt-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary animate-glow-pulse" />
              <span>{view === 'cz' ? mapTexts.homeBase : mapTexts.madeIn}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary/60" />
              <span>{lang === 'cs' ? 'Dodáváme' : 'We deliver'}</span>
            </div>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center text-muted-foreground mt-8 max-w-xl mx-auto"
        >
          {mapTexts.description}
        </motion.p>
      </div>
    </section>
  );
};

export default WorldMapSection;
